/**
 * useSessionDiscovery — Dynamically discovers or requests an active GPU node session from platform-core.
 */

import { useState, useEffect, useCallback } from "react";
import { getToken } from "@/lib/tokenStore";

export interface LiveKitSession {
  livekitUrl: string;
  roomName: string;
  hostToken: string;
  sessionId: string;
}

export type DiscoveryStatus =
  | "idle"
  | "discovering"
  | "provisioning"
  | "collision"
  | "ready"
  | "error";

export interface StreamCollisionInfo {
  activeDeviceId: string;
  startedAt?: string;
}

export interface DiscoveryResult {
  status: DiscoveryStatus;
  session: LiveKitSession | null;
  message: string;
  retry: () => void;
  eta: number;
  bootStage?: string;
  bootProgress?: number;
  streamCollision?: StreamCollisionInfo | null;
  requestTakeover?: () => Promise<void>;
  cancelCollision?: () => void;
}

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL || "https://api.streampersona.app";

export function useSessionDiscovery(): DiscoveryResult {
  const [status, setStatus] = useState<DiscoveryStatus>("idle");
  const [session, setSession] = useState<LiveKitSession | null>(null);
  const [message, setMessage] = useState<string>("");
  const [eta, setEta] = useState<number>(0);
  const [bootStage, setBootStage] = useState<string>("allocating_gpu");
  const [bootProgress, setBootProgress] = useState<number>(10);
  const [streamCollision, setStreamCollision] = useState<StreamCollisionInfo | null>(null);

  const discover = useCallback(async () => {
    const token = getToken();
    const licenseKey = token?.licenseKey;

    if (!token || !licenseKey) {
      setStatus("error");
      setMessage("No license found.");
      return;
    }

    const deviceId = token.deviceId || "desktop-client";
    setStreamCollision(null);
    setStatus("discovering");
    setMessage("Locating available GPU streaming node...");

    try {
      // 1. Check if an active session already exists for this device
      const activeRes = await fetch(
        `${GATEWAY_URL}/api/v1/faceshift/session/active?license_key=${encodeURIComponent(licenseKey)}&device_id=${encodeURIComponent(deviceId)}`,
        { method: "GET" }
      );

      if (activeRes.status === 200) {
        const data = await activeRes.json();
        if (data.livekit_url && data.room_name) {
          setSession({
            livekitUrl: data.livekit_url,
            roomName: data.room_name,
            hostToken: data.host_token || "",
            sessionId: data.session_id || "",
          });
          setStatus("ready");
          return;
        }
      }
      
      // 2. If 404, no active session running for this device: attempt to request/provision a node
      if (activeRes.status === 404) {
        const nodeRes = await fetch(`${GATEWAY_URL}/api/v1/faceshift/session/request-node`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            license_key: licenseKey,
            device_id: deviceId,
            force_takeover: false,
          }),
        });

        if (nodeRes.status === 409) {
          const colData = await nodeRes.json().catch(() => ({}));
          setStatus("collision");
          setMessage(colData.message || "StreamPersona is actively streaming on another device.");
          setStreamCollision({
            activeDeviceId: colData.active_device_id || "another device",
            startedAt: colData.started_at,
          });
          return;
        } else if (nodeRes.status === 401) {
          setStatus("error");
          setMessage("License not bound to this device. Please re-activate.");
          return;
        } else if (nodeRes.status === 403) {
          setStatus("error");
          setMessage("License has been revoked.");
          return;
        } else if (nodeRes.status === 404) {
          setStatus("error");
          setMessage("License key not found.");
          return;
        }

        if (nodeRes.ok) {
          const nodeData = await nodeRes.json();
          if (nodeData.status === "ready" && nodeData.livekit_url && nodeData.room_name && nodeData.host_token) {
            setSession({
              livekitUrl: nodeData.livekit_url,
              roomName: nodeData.room_name,
              hostToken: nodeData.host_token,
              sessionId: nodeData.session_id || "",
            });
            setStatus("ready");
            return;
          } else if (nodeData.status === "provisioning") {
            setEta(nodeData.eta || 60);
            if (nodeData.boot_stage) setBootStage(nodeData.boot_stage);
            if (typeof nodeData.boot_progress === "number") setBootProgress(nodeData.boot_progress);
            setStatus("provisioning");
            setMessage("Starting your dedicated cloud GPU node...");
            return;
          } else if (nodeData.status === "busy") {
            setStatus("error");
            setMessage(nodeData.queue_message || "Server is currently busy.");
            return;
          }
        }
      }

      // 3. Fallback: if platform-core returned an error (e.g. 500)
      const errData = await activeRes.json().catch(() => ({}));
      setStatus("error");
      setMessage(errData.error || `Service returned status ${activeRes.status}`);
    } catch (e) {
      const errStr = e instanceof Error ? e.message : "Connection failed";
      setStatus("error");
      setMessage(`Unable to connect to StreamPersona platform (${errStr})`);
    }
  }, []);

  useEffect(() => {
    discover();
  }, [discover]);

  // Smooth 1-second countdown for ETA when provisioning
  useEffect(() => {
    if (status !== "provisioning" || eta <= 0) return;
    const timer = setInterval(() => {
      setEta((prev) => (prev > 1 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [status, eta > 0]);

  // If in provisioning mode, poll session status every 5 seconds
  useEffect(() => {
    if (status !== "provisioning") return;
    
    let polls = 0;
    const token = getToken();
    const licenseKey = token?.licenseKey;
    if (!licenseKey) return;
    const deviceId = token.deviceId || "desktop-client";

    const interval = setInterval(async () => {
      polls++;
      if (polls > 100) { // ~8-10 minutes safety cap
        clearInterval(interval);
        setStatus("error");
        setMessage("GPU node provisioning timed out. Please try again.");
        return;
      }

      try {
        const nodeRes = await fetch(`${GATEWAY_URL}/api/v1/faceshift/session/request-node`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            license_key: licenseKey,
            device_id: deviceId,
          }),
        });

        if (nodeRes.status === 409) {
          clearInterval(interval);
          const colData = await nodeRes.json().catch(() => ({}));
          setStatus("collision");
          setMessage(colData.message || "StreamPersona is actively streaming on another device.");
          setStreamCollision({
            activeDeviceId: colData.active_device_id || "another device",
            startedAt: colData.started_at,
          });
          return;
        } else if (nodeRes.status === 401) {
          clearInterval(interval);
          setStatus("error");
          setMessage("License not bound to this device. Please re-activate.");
          return;
        } else if (nodeRes.status === 403) {
          clearInterval(interval);
          setStatus("error");
          setMessage("License has been revoked.");
          return;
        } else if (nodeRes.status === 404) {
          clearInterval(interval);
          setStatus("error");
          setMessage("License key not found.");
          return;
        }

        if (nodeRes.ok) {
          const nodeData = await nodeRes.json();
          if (nodeData.status === "ready" && nodeData.livekit_url && nodeData.room_name && nodeData.host_token) {
            clearInterval(interval);
            setSession({
              livekitUrl: nodeData.livekit_url,
              roomName: nodeData.room_name,
              hostToken: nodeData.host_token,
              sessionId: nodeData.session_id || "",
            });
            setStatus("ready");
          } else if (nodeData.status === "provisioning") {
            if (nodeData.eta) setEta(nodeData.eta);
            if (nodeData.boot_stage) setBootStage(nodeData.boot_stage);
            if (typeof nodeData.boot_progress === "number") setBootProgress(nodeData.boot_progress);
          } else if (nodeData.status === "busy") {
            clearInterval(interval);
            setStatus("error");
            setMessage(nodeData.queue_message || "Server is currently busy.");
          }
        }
      } catch {
        // Continue polling
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [status]);

  const requestTakeover = useCallback(async () => {
    const token = getToken();
    const licenseKey = token?.licenseKey;
    if (!token || !licenseKey) return;
    const deviceId = token.deviceId || "desktop-client";

    setStatus("discovering");
    setMessage("Transferring active stream to this device...");

    try {
      const nodeRes = await fetch(`${GATEWAY_URL}/api/v1/faceshift/session/request-node`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          license_key: licenseKey,
          device_id: deviceId,
          force_takeover: true,
        }),
      });

      if (nodeRes.ok) {
        const nodeData = await nodeRes.json();
        if (nodeData.status === "ready" && nodeData.livekit_url && nodeData.room_name && nodeData.host_token) {
          setSession({
            livekitUrl: nodeData.livekit_url,
            roomName: nodeData.room_name,
            hostToken: nodeData.host_token,
            sessionId: nodeData.session_id || "",
          });
          setStreamCollision(null);
          setStatus("ready");
          return;
        } else if (nodeData.status === "provisioning") {
          setEta(nodeData.eta || 60);
          if (nodeData.boot_stage) setBootStage(nodeData.boot_stage);
          if (typeof nodeData.boot_progress === "number") setBootProgress(nodeData.boot_progress);
          setStreamCollision(null);
          setStatus("provisioning");
          setMessage("Starting your dedicated cloud GPU node...");
          return;
        }
      }
      const err = await nodeRes.json().catch(() => ({}));
      setStatus("error");
      setMessage(err.message || err.error || "Transfer failed.");
    } catch (e) {
      const errStr = e instanceof Error ? e.message : "Connection failed";
      setStatus("error");
      setMessage(`Unable to transfer session (${errStr})`);
    }
  }, []);

  const cancelCollision = useCallback(() => {
    setStatus("error");
    setMessage("Stream transfer cancelled. Click retry when you are ready to stream from this device.");
    setStreamCollision(null);
  }, []);

  return {
    status,
    session,
    message,
    retry: discover,
    eta,
    bootStage,
    bootProgress,
    streamCollision,
    requestTakeover,
    cancelCollision,
  };
}
